import { useEffect, useState, type ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: ReactNode;
  /** A notice under the description (an AppAlert), e.g. SF-T-237's run still working on the task. */
  notice?: ReactNode;
  confirmLabel: string;
  /** The way out, when it says more than "Cancel" ("Keep queue", "Keep my draft"). */
  cancelLabel?: string;
  /** Red confirm button, for actions that remove something. */
  destructive?: boolean;
  submitting?: boolean;
  /** Keep the confirm button off, e.g. while the description is still loading. */
  confirmDisabled?: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

/**
 * Ask before an action that removes or closes something. Use this rather than `window.confirm`,
 * which is unstyled and is silently answered "no" in embedded browser panels.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  notice,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  submitting = false,
  confirmDisabled = false,
  onOpenChange,
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription className="max-h-[60vh] overflow-y-auto whitespace-pre-line">{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {notice}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            disabled={confirmDisabled}
            loading={submitting}
            variant={destructive ? 'destructive' : 'default'}
            onClick={(e) => {
              // Stay open while the request runs; the caller closes it on success.
              e.preventDefault();
              onConfirm();
            }}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  /** Defaults to "Confirm". */
  confirmLabel?: string;
  /** Defaults to "Cancel". */
  cancelLabel?: string;
  destructive?: boolean;
}

type Pending = ConfirmOptions & { resolve: (ok: boolean) => void };

let pending: Pending | null = null;
let notifyHost: (() => void) | null = null;

/**
 * The modal replacement for `window.confirm`: `if (!(await confirmDialog({ title: '…' }))) return;`.
 * Works anywhere, including hooks and plain functions, because `ConfirmDialogHost` is mounted once
 * at the app root. A second question while one is open is answered "no".
 */
export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  if (pending || !notifyHost) return Promise.resolve(false);
  return new Promise((resolve) => {
    pending = { ...options, resolve };
    notifyHost?.();
  });
}

/** Whether a question is on screen, so a popover behind it can stay open while it is answered. */
export function isConfirmDialogOpen(): boolean {
  return pending != null;
}

export function ConfirmDialogHost() {
  const [current, setCurrent] = useState<Pending | null>(null);
  // The wording stays while the dialog animates out.
  const [shown, setShown] = useState<ConfirmOptions | null>(null);
  useEffect(() => {
    notifyHost = () => {
      setCurrent(pending);
      if (pending) setShown(pending);
    };
    return () => {
      notifyHost = null;
    };
  }, []);

  const answer = (ok: boolean) => {
    if (!current) return;
    pending = null;
    setCurrent(null);
    current.resolve(ok);
  };

  return (
    <ConfirmDialog
      open={current != null}
      title={shown?.title ?? ''}
      description={shown?.description ?? ''}
      confirmLabel={shown?.confirmLabel ?? 'Confirm'}
      cancelLabel={shown?.cancelLabel}
      destructive={shown?.destructive}
      onOpenChange={(open) => !open && answer(false)}
      onConfirm={() => answer(true)}
    />
  );
}
